import { api } from './api';

class AIAnalysisService {
  /**
   * Generate AI-powered insights based on test performance and syllabus
   * @param {Object} analysisData - Test results, questions, and user data
   * @returns {Promise<Object>} AI-generated insights
   */
  async generateInsights(analysisData) {
    try {
      const { testResults, testData } = analysisData;
      
      // If we have testId and submissionId, try to call the backend AI service
      if (testData?.id && testResults?.submissionId) {
        try {
          const backendInsights = await this.callBackendAIService(testData.id, testResults.submissionId);
          if (backendInsights) {
            return backendInsights;
          }
        } catch (backendError) {
          console.warn('Backend AI service failed, falling back to client-side analysis:', backendError);
        }
      }
      
      // Fallback to client-side analysis
      return this.generateClientSideInsights(analysisData);
    } catch (error) {
      console.error('Failed to generate AI insights:', error);
      throw new Error('Unable to generate insights at this time');
    }
  }

  /**
   * Generate insights on the client side based on test performance
   * This is a fallback method that provides meaningful insights without requiring a backend AI service
   */
  generateClientSideInsights(data) {
    const { testResults, testData, questions, userAnswers } = data;
    const percentage = testResults.percentage || 0;
    const correctAnswers = testResults.correctAnswers || 0;
    const totalQuestions = questions?.length || testData?.totalQuestions || 0;
    
    console.log('Generating enhanced AI insights with data:', {
      percentage,
      correctAnswers,
      totalQuestions,
      questionsCount: questions?.length,
      userAnswersCount: Object.keys(userAnswers || {}).length
    });
    
    // Analyze performance by topic/subject with enhanced granularity
    const topicAnalysis = this.analyzeTopicPerformance(questions, userAnswers);
    const topicPerformance = topicAnalysis.topics || [];
    
    console.log('Enhanced topic analysis:', topicAnalysis);
    
    // Generate overall performance assessment
    const overallPerformance = this.assessOverallPerformance(percentage, correctAnswers, totalQuestions);
    
    // Identify strengths and weaknesses with enhanced data
    const strengths = this.identifyStrengths(topicPerformance, percentage, topicAnalysis.overallStats);
    const improvements = this.identifyImprovements(topicPerformance, percentage, topicAnalysis.overallStats);
    
    // Generate personalized recommendations with enhanced context
    const recommendations = this.generateRecommendations(improvements, strengths, testData);
    
    // Create next steps with priority and timeline
    const nextSteps = this.generateNextSteps(improvements, percentage, topicAnalysis.overallStats);

    const insights = {
      overallPerformance: {
        ...overallPerformance,
        consistencyScore: topicAnalysis.overallStats?.consistencyScore,
        topicDistribution: {
          strong: topicAnalysis.overallStats?.strongTopicsCount || 0,
          weak: topicAnalysis.overallStats?.weakTopicsCount || 0,
          total: topicAnalysis.overallStats?.totalTopics || 0
        }
      },
      strengths,
      improvements,
      recommendations,
      nextSteps,
      detailedAnalysis: {
        topicBreakdown: topicPerformance,
        subtopicAnalysis: topicAnalysis.subtopics || [],
        performancePatterns: this.identifyPerformancePatterns(topicPerformance),
        studyEfficiencyScore: this.calculateStudyEfficiencyScore(topicPerformance, percentage)
      },
      generatedAt: new Date().toISOString(),
      analysisVersion: '2.0'
    };

    console.log('Generated enhanced insights:', insights);
    return insights;
  }

  /**
   * Identify performance patterns across topics and difficulties
   */
  identifyPerformancePatterns(topicPerformance) {
    const patterns = [];
    
    // Check for difficulty-based patterns
    const difficultyPattern = this.analyzeDifficultyPatterns(topicPerformance);
    if (difficultyPattern) patterns.push(difficultyPattern);
    
    // Check for consistency patterns
    const consistencyPattern = this.analyzeConsistencyPatterns(topicPerformance);
    if (consistencyPattern) patterns.push(consistencyPattern);
    
    // Check for topic clustering patterns
    const clusteringPattern = this.analyzeTopicClustering(topicPerformance);
    if (clusteringPattern) patterns.push(clusteringPattern);
    
    return patterns;
  }

  /**
   * Analyze patterns in difficulty performance
   */
  analyzeDifficultyPatterns(topicPerformance) {
    const difficultyStats = { easy: [], medium: [], hard: [] };
    
    topicPerformance.forEach(topic => {
      if (topic.difficultyBreakdown) {
        Object.entries(topic.difficultyBreakdown).forEach(([difficulty, stats]) => {
          if (stats.total > 0) {
            difficultyStats[difficulty].push(stats.percentage);
          }
        });
      }
    });
    
    const patterns = [];
    
    // Check if struggling with easy questions
    const easyAvg = difficultyStats.easy.length > 0 
      ? difficultyStats.easy.reduce((sum, score) => sum + score, 0) / difficultyStats.easy.length 
      : 100;
    
    if (easyAvg < 70 && difficultyStats.easy.length > 0) {
      patterns.push({
        type: 'difficulty',
        pattern: 'struggling_with_basics',
        description: 'Missing fundamental concepts in easy questions',
        recommendation: 'Focus on reviewing basic concepts before advancing to harder topics'
      });
    }
    
    // Check if doing well on hard but poorly on easy/medium
    const hardAvg = difficultyStats.hard.length > 0 
      ? difficultyStats.hard.reduce((sum, score) => sum + score, 0) / difficultyStats.hard.length 
      : 0;
    
    if (hardAvg > easyAvg + 20 && difficultyStats.hard.length > 0) {
      patterns.push({
        type: 'difficulty',
        pattern: 'inconsistent_performance',
        description: 'Better performance on complex questions than basic ones',
        recommendation: 'Review fundamental concepts to build a stronger foundation'
      });
    }
    
    return patterns.length > 0 ? patterns[0] : null;
  }

  /**
   * Analyze consistency patterns across topics
   */
  analyzeConsistencyPatterns(topicPerformance) {
    if (topicPerformance.length < 3) return null;
    
    const scores = topicPerformance.map(topic => topic.score);
    const range = Math.max(...scores) - Math.min(...scores);
    
    if (range > 40) {
      return {
        type: 'consistency',
        pattern: 'high_variability',
        description: 'Performance varies significantly across topics',
        recommendation: 'Focus on bringing weaker topics up to the level of stronger ones'
      };
    }
    
    if (range < 15 && scores.every(score => score > 70)) {
      return {
        type: 'consistency',
        pattern: 'consistently_strong',
        description: 'Consistent strong performance across all topics',
        recommendation: 'Consider advancing to more challenging material'
      };
    }
    
    return null;
  }

  /**
   * Analyze topic clustering patterns
   */
  analyzeTopicClustering(topicPerformance) {
    // Group topics by performance level
    const excellent = topicPerformance.filter(t => t.score >= 90);
    const good = topicPerformance.filter(t => t.score >= 70 && t.score < 90);
    const needsWork = topicPerformance.filter(t => t.score < 70);
    
    if (needsWork.length > excellent.length + good.length) {
      return {
        type: 'clustering',
        pattern: 'multiple_weak_areas',
        description: 'Multiple topics need significant improvement',
        recommendation: 'Create a structured study plan to systematically address weak areas'
      };
    }
    
    return null;
  }

  /**
   * Calculate study efficiency score based on performance distribution
   */
  calculateStudyEfficiencyScore(topicPerformance, overallPercentage) {
    if (topicPerformance.length === 0) return 50;
    
    // Calculate how efficiently study time should be allocated
    const highPriorityTopics = topicPerformance.filter(t => t.studyPriority === 'high').length;
    const totalTopics = topicPerformance.length;
    
    // Efficiency is higher when there are fewer high-priority topics
    const focusScore = Math.max(0, 100 - (highPriorityTopics / totalTopics) * 100);
    
    // Combine with overall performance
    const efficiencyScore = Math.round((focusScore + overallPercentage) / 2);
    
    return Math.min(100, Math.max(0, efficiencyScore));
  }

  /**
   * Analyze performance by topic/subject area with enhanced granularity
   */
  analyzeTopicPerformance(questions, userAnswers) {
    if (!questions || !Array.isArray(questions)) return [];

    const topicStats = {};
    const subtopicStats = {};
    
    questions.forEach(question => {
      // Enhanced topic extraction with fallbacks and normalization
      const mainTopic = this.extractMainTopic(question);
      const subtopic = this.extractSubtopic(question);
      const difficulty = question.difficulty || this.inferDifficulty(question);
      const isCorrect = this.isAnswerCorrect(question, userAnswers[question.id]);
      
      // Track main topic performance
      if (!topicStats[mainTopic]) {
        topicStats[mainTopic] = {
          topic: mainTopic,
          total: 0,
          correct: 0,
          questions: [],
          subtopics: new Set(),
          difficulties: { easy: 0, medium: 0, hard: 0 },
          correctByDifficulty: { easy: 0, medium: 0, hard: 0 }
        };
      }
      
      topicStats[mainTopic].total++;
      topicStats[mainTopic].questions.push(question);
      topicStats[mainTopic].subtopics.add(subtopic);
      topicStats[mainTopic].difficulties[difficulty]++;
      
      if (isCorrect) {
        topicStats[mainTopic].correct++;
        topicStats[mainTopic].correctByDifficulty[difficulty]++;
      }
      
      // Track subtopic performance for detailed analysis
      const subtopicKey = `${mainTopic} - ${subtopic}`;
      if (!subtopicStats[subtopicKey]) {
        subtopicStats[subtopicKey] = {
          topic: mainTopic,
          subtopic: subtopic,
          total: 0,
          correct: 0,
          questions: []
        };
      }
      
      subtopicStats[subtopicKey].total++;
      subtopicStats[subtopicKey].questions.push(question);
      
      if (isCorrect) {
        subtopicStats[subtopicKey].correct++;
      }
    });

    // Convert to arrays and calculate enhanced metrics
    const topicPerformance = Object.values(topicStats).map(stat => {
      const score = Math.round((stat.correct / stat.total) * 100);
      const difficultyAnalysis = this.analyzeDifficultyPerformance(stat);
      
      return {
        ...stat,
        score,
        percentage: score,
        subtopics: Array.from(stat.subtopics),
        difficultyBreakdown: difficultyAnalysis,
        performanceLevel: this.getPerformanceLevel(score),
        improvementPotential: this.calculateImprovementPotential(stat),
        studyPriority: this.calculateStudyPriority(score, stat.total)
      };
    });

    // Add detailed subtopic analysis for topics with multiple subtopics
    const detailedSubtopics = Object.values(subtopicStats)
      .filter(stat => stat.total >= 2) // Only include subtopics with multiple questions
      .map(stat => ({
        ...stat,
        score: Math.round((stat.correct / stat.total) * 100),
        performanceLevel: this.getPerformanceLevel(Math.round((stat.correct / stat.total) * 100))
      }));

    // Sort by performance (worst first for prioritization)
    topicPerformance.sort((a, b) => a.score - b.score);
    
    return {
      topics: topicPerformance,
      subtopics: detailedSubtopics,
      overallStats: this.calculateOverallTopicStats(topicPerformance)
    };
  }

  /**
   * Extract main topic from question with better categorization
   */
  extractMainTopic(question) {
    // Try multiple fields for topic information
    let topic = question.topic || question.subject || question.category || question.chapter;
    
    if (!topic && question.tags && Array.isArray(question.tags)) {
      topic = question.tags[0];
    }
    
    if (!topic && question.content) {
      // Try to infer topic from question content (basic keyword matching)
      topic = this.inferTopicFromContent(question.content);
    }
    
    // Normalize and clean up topic name
    if (topic) {
      topic = topic.toString().trim();
      // Capitalize first letter of each word
      topic = topic.replace(/\b\w/g, l => l.toUpperCase());
    }
    
    return topic || 'General Knowledge';
  }

  /**
   * Extract subtopic for more granular analysis
   */
  extractSubtopic(question) {
    let subtopic = question.subtopic || question.subcategory || question.section;
    
    if (!subtopic && question.tags && Array.isArray(question.tags) && question.tags.length > 1) {
      subtopic = question.tags[1];
    }
    
    if (!subtopic) {
      // Try to infer subtopic from question type or format
      subtopic = this.inferSubtopicFromQuestion(question);
    }
    
    return subtopic || 'Core Concepts';
  }

  /**
   * Infer topic from question content using keyword matching
   */
  inferTopicFromContent(content) {
    const topicKeywords = {
      'Mathematics': ['equation', 'solve', 'calculate', 'formula', 'number', 'algebra', 'geometry'],
      'Science': ['experiment', 'hypothesis', 'theory', 'molecule', 'atom', 'energy', 'force'],
      'English': ['grammar', 'sentence', 'paragraph', 'literature', 'author', 'poem', 'essay'],
      'History': ['year', 'century', 'war', 'empire', 'revolution', 'ancient', 'medieval'],
      'Geography': ['country', 'continent', 'ocean', 'mountain', 'climate', 'population', 'capital']
    };
    
    const lowerContent = content.toLowerCase();
    
    for (const [topic, keywords] of Object.entries(topicKeywords)) {
      if (keywords.some(keyword => lowerContent.includes(keyword))) {
        return topic;
      }
    }
    
    return null;
  }

  /**
   * Infer subtopic from question characteristics
   */
  inferSubtopicFromQuestion(question) {
    if (question.type === 'multiple-choice') return 'Multiple Choice';
    if (question.type === 'true-false') return 'True/False';
    if (question.type === 'short-answer') return 'Short Answer';
    if (question.type === 'essay') return 'Essay';
    
    // Infer from question format or content patterns
    if (question.content && question.content.includes('?')) {
      if (question.content.toLowerCase().includes('explain')) return 'Explanation';
      if (question.content.toLowerCase().includes('compare')) return 'Comparison';
      if (question.content.toLowerCase().includes('analyze')) return 'Analysis';
    }
    
    return 'General';
  }

  /**
   * Infer difficulty level from question characteristics
   */
  inferDifficulty(question) {
    // Check if difficulty is explicitly set
    if (question.difficulty) return question.difficulty;
    
    // Infer from question characteristics
    const content = question.content || '';
    const hasMultipleSteps = content.split('.').length > 2;
    const hasComplexVocabulary = content.length > 200;
    const isAnalytical = /analyze|evaluate|synthesize|compare|contrast/i.test(content);
    
    if (isAnalytical || hasComplexVocabulary) return 'hard';
    if (hasMultipleSteps) return 'medium';
    return 'easy';
  }

  /**
   * Analyze performance across different difficulty levels
   */
  analyzeDifficultyPerformance(topicStat) {
    const analysis = {};
    
    ['easy', 'medium', 'hard'].forEach(difficulty => {
      const total = topicStat.difficulties[difficulty];
      const correct = topicStat.correctByDifficulty[difficulty];
      
      if (total > 0) {
        analysis[difficulty] = {
          total,
          correct,
          percentage: Math.round((correct / total) * 100),
          needsWork: (correct / total) < 0.7
        };
      }
    });
    
    return analysis;
  }

  /**
   * Get performance level description
   */
  getPerformanceLevel(score) {
    if (score >= 90) return 'excellent';
    if (score >= 80) return 'good';
    if (score >= 70) return 'satisfactory';
    if (score >= 60) return 'needs improvement';
    return 'requires attention';
  }

  /**
   * Calculate improvement potential based on question difficulty distribution
   */
  calculateImprovementPotential(topicStat) {
    const easyIncorrect = topicStat.difficulties.easy - topicStat.correctByDifficulty.easy;
    const mediumIncorrect = topicStat.difficulties.medium - topicStat.correctByDifficulty.medium;
    
    if (easyIncorrect > 0) return 'high'; // Missing easy questions = high potential
    if (mediumIncorrect > 0) return 'medium';
    return 'low';
  }

  /**
   * Calculate study priority based on score and question count
   */
  calculateStudyPriority(score, questionCount) {
    if (score < 60 && questionCount >= 3) return 'high';
    if (score < 75 && questionCount >= 2) return 'medium';
    return 'low';
  }

  /**
   * Calculate overall statistics across all topics
   */
  calculateOverallTopicStats(topicPerformance) {
    const totalQuestions = topicPerformance.reduce((sum, topic) => sum + topic.total, 0);
    const totalCorrect = topicPerformance.reduce((sum, topic) => sum + topic.correct, 0);
    const averageScore = Math.round((totalCorrect / totalQuestions) * 100);
    
    const strongTopics = topicPerformance.filter(topic => topic.score >= 80);
    const weakTopics = topicPerformance.filter(topic => topic.score < 60);
    
    return {
      totalTopics: topicPerformance.length,
      averageScore,
      strongTopicsCount: strongTopics.length,
      weakTopicsCount: weakTopics.length,
      mostImprovedPotential: topicPerformance.find(topic => topic.improvementPotential === 'high')?.topic,
      consistencyScore: this.calculateConsistencyScore(topicPerformance)
    };
  }

  /**
   * Calculate consistency score across topics
   */
  calculateConsistencyScore(topicPerformance) {
    if (topicPerformance.length < 2) return 100;
    
    const scores = topicPerformance.map(topic => topic.score);
    const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const variance = scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) / scores.length;
    const standardDeviation = Math.sqrt(variance);
    
    // Convert to consistency score (lower deviation = higher consistency)
    return Math.max(0, Math.round(100 - (standardDeviation * 2)));
  }

  /**
   * Check if a user's answer is correct
   */
  isAnswerCorrect(question, userAnswer) {
    if (!question.options || !userAnswer) return false;
    const correctOption = question.options.find(opt => opt.isCorrect);
    return correctOption && correctOption.id === userAnswer;
  }

  /**
   * Assess overall performance level
   */
  assessOverallPerformance(percentage, correctAnswers, totalQuestions) {
    let level, description;

    if (percentage >= 90) {
      level = 'Excellent';
      description = `Outstanding performance! You scored ${percentage}% (${correctAnswers}/${totalQuestions}), demonstrating exceptional mastery of the subject matter. Your strong understanding across all areas is commendable.`;
    } else if (percentage >= 80) {
      level = 'Good';
      description = `Great job! You scored ${percentage}% (${correctAnswers}/${totalQuestions}), showing solid understanding of most concepts. With a little more focus on specific areas, you can achieve even better results.`;
    } else if (percentage >= 70) {
      level = 'Average';
      description = `You scored ${percentage}% (${correctAnswers}/${totalQuestions}), which shows a reasonable grasp of the material. There's room for improvement in several key areas that could significantly boost your performance.`;
    } else if (percentage >= 60) {
      level = 'Needs Improvement';
      description = `You scored ${percentage}% (${correctAnswers}/${totalQuestions}). While you've grasped some concepts, focused study on fundamental topics will help strengthen your overall understanding.`;
    } else {
      level = 'Needs Significant Improvement';
      description = `You scored ${percentage}% (${correctAnswers}/${totalQuestions}). This indicates a need for comprehensive review of the core concepts. Don't be discouraged - with dedicated study, you can make substantial improvements.`;
    }

    return { level, description, percentage };
  }

  /**
   * Identify student's strengths based on performance
   */
  identifyStrengths(topicPerformance, overallPercentage) {
    const strengths = [];
    
    // Find topics where student performed well (above 80%)
    const strongTopics = topicPerformance.filter(topic => topic.percentage >= 80);
    
    strongTopics.forEach(topic => {
      strengths.push({
        topic: topic.topic,
        score: topic.percentage,
        description: `You demonstrated strong understanding in ${topic.topic}, correctly answering ${topic.correct} out of ${topic.total} questions.`
      });
    });

    // If no strong topics, identify relative strengths
    if (strengths.length === 0 && topicPerformance.length > 0) {
      const bestTopic = topicPerformance.reduce((best, current) => 
        current.percentage > best.percentage ? current : best
      );
      
      strengths.push({
        topic: bestTopic.topic,
        score: bestTopic.percentage,
        description: `${bestTopic.topic} was your strongest area, where you scored ${bestTopic.percentage}%. Building on this foundation can help improve other areas.`
      });
    }

    // Add general strengths based on overall performance
    if (overallPercentage >= 70) {
      strengths.push({
        area: 'Test-Taking Skills',
        description: 'You showed good test-taking abilities and time management during the assessment.'
      });
    }

    return strengths;
  }

  /**
   * Identify areas that need improvement
   */
  identifyImprovements(topicPerformance, overallPercentage) {
    const improvements = [];
    
    // Find topics where student performed poorly (below 70%)
    const weakTopics = topicPerformance.filter(topic => topic.percentage < 70);
    
    // Sort by lowest performance first
    weakTopics.sort((a, b) => a.percentage - b.percentage);
    
    weakTopics.slice(0, 3).forEach(topic => { // Limit to top 3 areas for focus
      improvements.push({
        topic: topic.topic,
        score: topic.percentage,
        description: `Focus on ${topic.topic} concepts. You answered ${topic.correct} out of ${topic.total} questions correctly (${topic.percentage}%). This area needs additional study and practice.`
      });
    });

    // Add general improvement areas based on overall performance
    if (overallPercentage < 80) {
      improvements.push({
        area: 'Time Management',
        description: 'Consider practicing time management strategies to ensure you can complete all questions within the allotted time.'
      });
    }

    if (overallPercentage < 60) {
      improvements.push({
        area: 'Fundamental Concepts',
        description: 'Review the basic principles and foundational concepts before moving to advanced topics.'
      });
    }

    return improvements;
  }

  /**
   * Generate personalized study recommendations
   */
  generateRecommendations(improvements, strengths, testData) {
    const recommendations = [];
    const subject = testData?.subject || testData?.title || 'the subject';
    const testType = testData?.type || 'practice test';
    
    // Enhanced recommendations based on weak areas with specific study strategies
    improvements.forEach((improvement, index) => {
      if (improvement.topic) {
        const priority = improvement.score < 40 ? 'high' : improvement.score < 60 ? 'medium' : 'low';
        const timeEstimate = improvement.score < 40 ? '2-3 hours daily' : improvement.score < 60 ? '1-2 hours daily' : '30-60 minutes daily';
        
        recommendations.push({
          title: `Master ${improvement.topic} Fundamentals`,
          description: `Focus on strengthening your understanding of ${improvement.topic}. Current mastery: ${improvement.score}%. Target: 80%+`,
          priority,
          timeEstimate,
          difficulty: improvement.score < 40 ? 'challenging' : 'moderate',
          resources: this.getSpecificResources(improvement.topic, subject, improvement.score),
          actionItems: [
            `Review ${improvement.topic} theory and key concepts`,
            `Complete 10-15 practice problems daily`,
            `Create summary notes for quick revision`,
            `Test understanding with mini-quizzes`
          ],
          expectedImpact: `Improve ${improvement.topic} score by 15-25 points`
        });
      }
    });

    // Study method recommendations based on performance patterns
    const overallScore = improvements.reduce((sum, imp) => sum + imp.score, 0) / improvements.length;
    
    if (overallScore < 60) {
      recommendations.push({
        title: 'Implement Active Learning Strategies',
        description: 'Your performance suggests you need more engaging study methods to improve retention and understanding.',
        priority: 'high',
        timeEstimate: '30 minutes setup, ongoing practice',
        difficulty: 'easy to moderate',
        resources: [
          'Use flashcards for key terms and formulas',
          'Teach concepts to someone else or explain aloud',
          'Create mind maps connecting related topics',
          'Use the Feynman Technique for complex concepts'
        ],
        actionItems: [
          'Replace passive reading with active note-taking',
          'Quiz yourself regularly without looking at notes',
          'Form study groups for discussion and peer teaching',
          'Use spaced repetition for long-term retention'
        ],
        expectedImpact: 'Improve overall retention by 20-30%'
      });
    }

    // Time management and test strategy recommendations
    recommendations.push({
      title: 'Optimize Test-Taking Strategy',
      description: `Develop effective strategies for ${testType} to maximize your performance under time pressure.`,
      priority: 'medium',
      timeEstimate: '1 hour practice sessions',
      difficulty: 'easy',
      resources: [
        'Practice with timed mock tests weekly',
        'Learn to identify question types quickly',
        'Master elimination techniques for multiple choice',
        'Develop a systematic approach to problem-solving'
      ],
      actionItems: [
        'Time yourself on individual questions',
        'Practice skipping difficult questions and returning later',
        'Review common question patterns and formats',
        'Develop pre-test routines to reduce anxiety'
      ],
      expectedImpact: 'Improve test efficiency and reduce time pressure'
    });

    // Leverage strengths for cross-topic learning
    if (strengths.length > 0) {
      const strongestTopic = strengths[0];
      recommendations.push({
        title: `Leverage Your ${strongestTopic.topic || strongestTopic.area} Strength`,
        description: `Use your excellent understanding of ${strongestTopic.topic || strongestTopic.area} (${strongestTopic.score}% mastery) to build confidence and learn related concepts.`,
        priority: 'low',
        timeEstimate: '15-30 minutes daily',
        difficulty: 'easy',
        resources: [
          `Find connections between ${strongestTopic.topic || strongestTopic.area} and weaker topics`,
          'Use analogies from strong areas to understand difficult concepts',
          'Tutor others in your strong subjects to reinforce knowledge',
          'Build on existing knowledge to tackle advanced topics'
        ],
        actionItems: [
          'Create concept maps showing topic relationships',
          'Use your strength as a confidence booster before studying weak areas',
          'Look for patterns and similarities across topics',
          'Apply problem-solving methods from strong areas to weak ones'
        ],
        expectedImpact: 'Boost confidence and accelerate learning in weak areas'
      });
    }

    // Subject-specific advanced recommendations
    if (improvements.length > 2) {
      recommendations.push({
        title: 'Create a Personalized Study Plan',
        description: 'With multiple areas needing attention, a structured approach will maximize your improvement.',
        priority: 'high',
        timeEstimate: '2-3 hours total study time daily',
        difficulty: 'moderate',
        resources: [
          'Weekly study schedule with topic rotation',
          'Progress tracking spreadsheet or app',
          'Regular self-assessment quizzes',
          'Milestone rewards for achieving targets'
        ],
        actionItems: [
          'Allocate 60% time to weakest topics, 30% to moderate, 10% to strengths',
          'Set weekly improvement targets for each topic',
          'Schedule regular review sessions for all topics',
          'Track daily progress and adjust plan as needed'
        ],
        expectedImpact: 'Systematic improvement across all weak areas'
      });
    }

    return recommendations.slice(0, 5); // Limit to top 5 most relevant recommendations
  }

  /**
   * Get specific study resources based on topic and subject
   */
  getSpecificResources(topic, subject, currentScore) {
    const resources = [];
    const isBasicLevel = currentScore < 50;
    const isIntermediateLevel = currentScore >= 50 && currentScore < 70;
    
    // Basic level resources
    if (isBasicLevel) {
      resources.push(
        `Start with ${topic} basics: definitions, key terms, and simple examples`,
        `Watch introductory videos on ${topic} concepts`,
        `Complete beginner-level worksheets and exercises`,
        `Use visual aids like diagrams and charts for ${topic}`
      );
    }
    
    // Intermediate level resources
    if (isIntermediateLevel) {
      resources.push(
        `Practice intermediate ${topic} problems with step-by-step solutions`,
        `Review ${topic} examples from textbook and class notes`,
        `Join online forums or study groups focused on ${topic}`,
        `Create flashcards for ${topic} formulas and key concepts`
      );
    }
    
    // Advanced level resources (for fine-tuning)
    if (currentScore >= 70) {
      resources.push(
        `Tackle challenging ${topic} problems and past exam questions`,
        `Explore real-world applications of ${topic} concepts`,
        `Teach ${topic} to others to reinforce understanding`,
        `Research advanced ${topic} topics for deeper insight`
      );
    }
    
    // Subject-specific resources
    if (subject.toLowerCase().includes('math') || subject.toLowerCase().includes('mathematics')) {
      resources.push(`Use Khan Academy or similar platforms for ${topic} practice`);
      resources.push(`Work through ${topic} problem sets with increasing difficulty`);
    } else if (subject.toLowerCase().includes('science')) {
      resources.push(`Conduct ${topic} experiments or simulations if possible`);
      resources.push(`Review ${topic} laboratory procedures and results`);
    } else if (subject.toLowerCase().includes('english') || subject.toLowerCase().includes('language')) {
      resources.push(`Read additional texts related to ${topic}`);
      resources.push(`Practice ${topic} through writing exercises and analysis`);
    }
    
    return resources.slice(0, 4); // Limit to 4 most relevant resources
  }

  /**
   * Generate actionable next steps
   */
  generateNextSteps(improvements, overallPercentage) {
    const nextSteps = [];
    
    if (overallPercentage < 60) {
      nextSteps.push({
        title: 'Review Fundamental Concepts',
        description: 'Start with a comprehensive review of basic concepts before attempting advanced problems.',
        timeframe: '1-2 weeks'
      });
    }

    if (improvements.length > 0) {
      const topWeakness = improvements[0];
      nextSteps.push({
        title: `Focus on ${topWeakness.topic || topWeakness.area}`,
        description: `Prioritize studying ${topWeakness.topic || topWeakness.area} as it showed the most room for improvement.`,
        timeframe: '3-5 days'
      });
    }

    nextSteps.push({
      title: 'Take Another Practice Test',
      description: 'After studying your weak areas, take another practice test to measure your improvement.',
      timeframe: '1 week'
    });

    if (overallPercentage >= 70) {
      nextSteps.push({
        title: 'Challenge Yourself',
        description: 'Try more advanced questions or timed practice sessions to push your performance to the next level.',
        timeframe: 'Ongoing'
      });
    }

    return nextSteps;
  }

  /**
   * Call backend AI service for enhanced insights
   * @param {string} testId - Test ID
   * @param {string} submissionId - Test result submission ID
   * @returns {Promise<Object>} AI-generated insights from backend
   */
  async callBackendAIService(testId, submissionId) {
    try {
      console.log(`Calling backend AI service for test ${testId}, submission ${submissionId}`);
      
      const response = await api.post(`/tests/${testId}/results/${submissionId}/ai-analysis`);
      
      if (response.data?.success && response.data?.data?.insights) {
        const insights = response.data.data.insights;
        
        // Add metadata about the analysis
        insights.analysisMetadata = {
          source: 'backend-ai',
          syllabusAvailable: response.data.data.analysisData?.syllabusAvailable || false,
          analyzedAt: response.data.data.analysisData?.analyzedAt,
          version: '2.0'
        };
        
        return insights;
      }
      
      return null;
    } catch (error) {
      console.error('Backend AI service error:', error);
      
      // If it's a 404 or 403, don't retry with client-side
      if (error.response?.status === 404 || error.response?.status === 403) {
        throw error;
      }
      
      // For other errors, allow fallback to client-side
      return null;
    }
  }
}

export const aiAnalysisService = new AIAnalysisService();